import { ModalConfirmation, notify, useModalMultiple, withModalConditionalRender } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router'

import { VoidBillConfirmationModal as VoidBillConfirmationModalComponent } from '@features/bills/elements/VoidBillConfirmationModal'
import { VoidBillPaymentConfirmationModal as VoidBillPaymentConfirmationModalComponent } from '@features/bills/elements/VoidBillPaymentConfirmationModal'
import { useGetBill } from '@features/bills/hooks/useGetBill'
import { useRegisterBillPayment } from '@features/bills/hooks/useRegisterBillPayment'
import { useFetchDaybookTransaction } from '@features/daybook/hooks/useFetchDaybookTransaction'
import { useInvalidateDaybookTransaction } from '@features/daybook/hooks/useInvalidateDaybookTransaction'
import {
  useVoidDaybookTransaction,
  VoidDaybookTransactionMode,
} from '@features/daybook/hooks/useVoidDaybookTransaction'
import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useVatDeclaration } from '@features/vatDeclarations/hooks/useVatDeclaration'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { EmberRoute } from '../../../../../enums/emberRoute'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useSegment } from '../../../../../hooks'
import { useEmberRouteToUrlGenerator } from '../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useEmberTransitionTo } from '../../../../../hooks/routing/useEmberTransitionTo'
import { useCopyAndNotify } from '../../../../../hooks/useCopyAndNotify'
import { APIError } from '../../../../../utils'
import { BillReconciliationModal as BillReconciliationModalComponent } from '../../../../bills/elements/BillReconciliationModal'
import { useInvoice } from '../../../../invoices'
import { InvoiceReconciliationModal as InvoiceReconciliationModalComponent } from '../../../../invoices/elements/InvoiceReconclliationModal/InvoiceReconciliationModal'
import { VoidInvoiceModal as VoidInvoiceModalComponent } from '../../../../invoices/elements/VoidInvoiceModal'
import { VoidCreditNoteModal as VoidCreditNoteModalComponent } from '../../../../invoices/routes/InvoicesList/elements/VoidCreditNoteModal'
import { useVoidInvoice } from '../../../../invoices/routes/InvoicesList/hooks/useVoidInvoice'
import { useVoidVatDeclarationPayment } from '../../../../vatDeclarations/elements/VatDeclarationsVoidPeriodPaymentConfirmationModal/hooks/useVoidVatDeclarationPayment'
import { useUpdateVatDeclarationSettlement } from '../../../../vatDeclarations/hooks/useUpdateVatDeclarationSettlement'
import { fetchVatDeclarationPayments } from '../../../../vatDeclarations/query-api'
import { TransactionsStaticActionItem } from '../../../types/transactionsStaticActionItem'
import { resolveStaticActionHandler } from '../elements/TransactionsTableStaticActions/utils/resolveStaticActionHandler'
import { getTransactionsStaticActions, TransactionsCommonStaticActionType } from '../utils/getTransactionsStaticActions'

const VoidBillConfirmationModal = withModalConditionalRender(VoidBillConfirmationModalComponent)
const BillReconciliationModal = withModalConditionalRender(BillReconciliationModalComponent)
const VoidInvoiceModal = withModalConditionalRender(VoidInvoiceModalComponent)
const VoidBillPaymentConfirmationModal = withModalConditionalRender(VoidBillPaymentConfirmationModalComponent)
const VoidCreditNoteModal = withModalConditionalRender(VoidCreditNoteModalComponent)
const InvoiceReconciliationModal = withModalConditionalRender(InvoiceReconciliationModalComponent)

const getVoidBillModalId = (id: string) => `${id}-void-bill-confirmation`
const getBillReconciliationModalId = (id: string) => `${id}-bill-reconciliation-static-action`
const getVoidInvoiceModalId = (id: string) => `${id}-void-invoice-static-action`
const getVoidBillPaymentModalId = (id: string) => `${id}-void-bill-payment-static-action`
const getVoidCreditNoteModalId = (id: string) => `${id}-void-credit-note-modal`
const getVoidVatDeclarationPaymentModalId = (id: string) => `${id}-void-vat-declaration-payment-static-action`
const getVoidVatDeclarationModalId = (id: string) => `${id}-void-vat-declaration-static-action`
const getInvoiceReconciliationModalId = (id: string) => `${id}-invoice-reconciliation-static-action`
const getVoidDaybookTransactionModalId = (id: string) => `${id}-void-daybook-transaction-static-action`
const getReverseDaybookTransactionModalId = (id: string) => `${id}-reverse-daybook-transaction-static-action`

interface UseTransactionStaticActionsProps {
  isVoided?: boolean
  originatorId: string
  originatorType?: TransactionOriginatorType
  transactionId: string
}

interface UseTransactionStaticActionsResult {
  handleSelect: <TValue extends string>(value: TValue) => void
  items: TransactionsStaticActionItem[]
  modals: ReactElement
}

export const useTransactionStaticActions = ({
  isVoided,
  originatorId,
  originatorType,
  transactionId,
}: UseTransactionStaticActionsProps): UseTransactionStaticActionsResult => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const location = useLocation()
  const { organization } = useUserOrganization()
  const { transitionTo } = useEmberTransitionTo()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const { copy } = useCopyAndNotify()

  const { data: billData } = useGetBill(originatorType === TransactionOriginatorType.Bill ? originatorId : undefined, {
    refetchOnMount: false,
  })
  const { data: invoiceData } = useInvoice(
    originatorType === TransactionOriginatorType.Invoice ? originatorId : undefined,
  )
  const { vatDeclaration } = useVatDeclaration({
    vatDeclarationId: originatorType === TransactionOriginatorType.SalesTaxReturn ? originatorId : undefined,
  })
  const { daybookTransaction } = useFetchDaybookTransaction(
    originatorType === TransactionOriginatorType.DaybookTransaction ? originatorId : undefined,
  )

  const { data: vatDeclarationPaymentsData } = useQuery(
    [QueryKeys.VatDeclarationsPayments, originatorId],
    () => fetchVatDeclarationPayments(originatorId),
    {
      enabled: originatorType === TransactionOriginatorType.SalesTaxReturn,
    },
  )

  const organizationUrl = organization?.url

  const bill = billData?.bill
  const invoice = invoiceData?.invoice

  const isBillPaid = bill?.isPaid
  const isInvoicePaid = invoice?.isPaid
  const isInvoiceCreditNote = invoice?.type === InvoiceType.CreditNote
  const isVATPeriodSettled = vatDeclaration?.isSettled
  const isVATPeriodPaid = vatDeclaration?.isPaid
  const daybookId = daybookTransaction?.daybookId

  const items = getTransactionsStaticActions({
    daybookId,
    isBillPaid,
    isInvoicePaid,
    isVATPeriodSettled,
    isVATPeriodPaid,
    isVoided,
    transactionType: originatorType,
    t,
  }) as TransactionsStaticActionItem[]

  const voidBillModalId = getVoidBillModalId(originatorId)
  const billReconciliationModalId = getBillReconciliationModalId(originatorId)
  const voidInvoiceModalId = getVoidInvoiceModalId(originatorId)
  const voidBillPaymentModalId = getVoidBillPaymentModalId(originatorId)
  const voidVatDeclarationPaymentModalId = getVoidVatDeclarationPaymentModalId(originatorId)
  const voidVatDeclarationModalId = getVoidVatDeclarationModalId(originatorId)
  const voidCreditNoteModalId = getVoidCreditNoteModalId(originatorId)
  const invoiceReconciliationModalId = getInvoiceReconciliationModalId(originatorId)
  const voidDaybookTransactionModalId = getVoidDaybookTransactionModalId(originatorId)
  const reverseDaybookTransactionModalId = getReverseDaybookTransactionModalId(originatorId)

  const { open: openModal, close: closeModal } = useModalMultiple([
    billReconciliationModalId,
    invoiceReconciliationModalId,
    voidBillModalId,
    voidInvoiceModalId,
    voidBillPaymentModalId,
    voidVatDeclarationPaymentModalId,
    voidVatDeclarationModalId,
    voidCreditNoteModalId,
  ])

  const registerPaymentForBill = useCallback(() => {
    openModal(billReconciliationModalId)
  }, [billReconciliationModalId, openModal])

  const { handleRegisterPayment: handleRegisterPaymentForBill } = useRegisterBillPayment({
    bill,
    openReconciliationModal: registerPaymentForBill,
  })

  const { invalidateDaybookTransaction } = useInvalidateDaybookTransaction()
  const { invalidateInvoice } = useInvalidateInvoices()

  const buildRecreateTransactionUrl = useCallback(
    (copyAttachments: boolean) => {
      if (!generateUrl || !daybookId) {
        return
      }

      /* Legacy Ember Daybook behaviour, will be changed fairly soon with full reactification */
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const params = new URLSearchParams({ recreate_transaction: originatorId })
      if (!copyAttachments) {
        params.set('copy_attachments', 'false')
      }

      return `${generateUrl(EmberRoute.Daybook, daybookId)}?${params.toString()}`
    },
    [daybookId, generateUrl, originatorId],
  )

  const navigateToRecreateTransaction = useCallback(() => {
    const url = buildRecreateTransactionUrl(true)
    if (url) {
      transitionTo(url)
    }
  }, [buildRecreateTransactionUrl, transitionTo])

  const handleCopyToDaybook = useCallback(() => {
    const url = buildRecreateTransactionUrl(false)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }, [buildRecreateTransactionUrl])

  const { voidTransaction: submitDaybookTransactionAction, isProcessing: isProcessingDaybookTransactionAction } =
    useVoidDaybookTransaction({
      onSuccess: (data, { mode }) => {
        queryClient.invalidateQueries(QueryKeys.Transactions)
        invalidateDaybookTransaction(originatorId)
        queryClient.invalidateQueries([QueryKeys.Transaction, transactionId])

        if (mode === VoidDaybookTransactionMode.Reverse) {
          closeModal(reverseDaybookTransactionModalId)
          notify({
            id: NotificationKeys.DaybookTransactionReversed,
            message: t('journal.reverse.success'),
            variant: 'success',
          })
          navigateToRecreateTransaction()
        } else {
          closeModal(voidDaybookTransactionModalId)
          notify({
            id: NotificationKeys.DaybookTransactionVoided,
            message: t('journal.void.success'),
            variant: 'success',
          })
        }
      },
      onError: (error, { mode }) => {
        if (mode === VoidDaybookTransactionMode.Reverse) {
          closeModal(reverseDaybookTransactionModalId)
          notify({
            id: NotificationKeys.DaybookTransactionReverseFailed,
            message: error?.message || t('journal.reverse.error'),
            variant: 'error',
          })
        } else {
          closeModal(voidDaybookTransactionModalId)
          notify({
            id: NotificationKeys.DaybookTransactionVoidFailed,
            message: error?.message || t('journal.void.error'),
            variant: 'error',
          })
        }
      },
    })

  const handleVoidDaybookTransaction = useCallback(() => {
    submitDaybookTransactionAction({ daybookTransactionId: originatorId, mode: VoidDaybookTransactionMode.Void })
  }, [originatorId, submitDaybookTransactionAction])

  const handleReverseDaybookTransaction = useCallback(() => {
    submitDaybookTransactionAction({ daybookTransactionId: originatorId, mode: VoidDaybookTransactionMode.Reverse })
  }, [originatorId, submitDaybookTransactionAction])

  const { voidPeriodPayment, isProcessing: isProcessingVoidPayment } = useVoidVatDeclarationPayment({
    onError: (error?: APIError) => {
      closeModal(voidVatDeclarationPaymentModalId)
      queryClient.invalidateQueries(QueryKeys.Transactions)
      notify({
        id: NotificationKeys.VatDeclarationsVoidPayment,
        message: error?.body?.errors[0]?.detail || t('vat_declarations.void_payment_modal.fail'),
        variant: 'error',
      })
    },
    onSuccess: () => {
      closeModal(voidVatDeclarationPaymentModalId)
      queryClient.invalidateQueries(QueryKeys.Transactions)
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      queryClient.invalidateQueries(QueryKeys.TaxReturnPeriods)
      queryClient.invalidateQueries([QueryKeys.Transaction, transactionId])
      notify({
        id: NotificationKeys.VatDeclarationsVoidPayment,
        message: t('vat_declarations.void_payment_modal.success'),
        variant: 'success',
      })
    },
  })

  const { update: updatePeriodSettlement, isProcessing: isProcessingVoidSettlement } =
    useUpdateVatDeclarationSettlement({
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKeys.Transactions)
        queryClient.invalidateQueries(QueryKeys.VatDeclarations)
        queryClient.invalidateQueries(QueryKeys.TaxReturnPeriods)
        queryClient.invalidateQueries([QueryKeys.Transaction, transactionId])
        closeModal(voidVatDeclarationModalId)
        notify({
          id: NotificationKeys.VatDeclarationsVoidSettlement,
          message: t('vat_declarations.void_settlement_modal.success'),
          variant: 'success',
        })
      },
      onError: (error?: APIError) => {
        closeModal(voidVatDeclarationModalId)
        notify({
          id: NotificationKeys.VatDeclarationsVoidSettlement,
          message: error?.body?.errorMessage || t('vat_declarations.void_settlement_modal.fail'),
          variant: 'error',
        })
      },
    })

  const { voidInvoice } = useVoidInvoice({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.Transactions)
      invalidateInvoice(originatorId)
      queryClient.invalidateQueries([QueryKeys.Transaction, transactionId])
      notify({
        id: NotificationKeys.InvoiceVoided,
        message: t('invoices.void_document.success'),
        variant: 'success',
      })
      closeModal(voidInvoiceModalId)
    },
    onError: (error?: APIError) => {
      notify({
        id: NotificationKeys.InvoiceVoided,
        message: error?.message || t('error'),
        variant: 'error',
      })
    },
  })

  const handleVoidVatDeclarationClick = useCallback(() => {
    updatePeriodSettlement({ id: originatorId, isSettled: false })
  }, [originatorId, updatePeriodSettlement])

  const voidVatDeclarationPayment = useCallback(() => {
    voidPeriodPayment({ vatDeclarationId: originatorId })
  }, [originatorId, voidPeriodPayment])

  const voidPaymentForVatDeclaration = useCallback(() => {
    if (vatDeclarationPaymentsData?.salesTaxPayments && vatDeclarationPaymentsData?.salesTaxPayments.length === 1) {
      voidPeriodPayment({ vatDeclarationId: vatDeclarationPaymentsData?.salesTaxPayments[0].id })
    } else {
      closeModal(voidVatDeclarationPaymentModalId)

      notify({
        id: NotificationKeys.VatDeclarationsVoidPayment,
        message: t('vat_declarations.void_payment_modal.fail.missing_id'),
        variant: 'error',
      })
    }
  }, [closeModal, vatDeclarationPaymentsData?.salesTaxPayments, t, voidPeriodPayment, voidVatDeclarationPaymentModalId])

  const handleVoidVatDeclarationPaymentClick = useCallback(() => {
    if (originatorType === TransactionOriginatorType.SalesTaxReturn) {
      voidPaymentForVatDeclaration()
    }
    if (originatorType === TransactionOriginatorType.SalesTaxPayment) {
      voidVatDeclarationPayment()
    }
  }, [originatorType, voidPaymentForVatDeclaration, voidVatDeclarationPayment])

  const handleNavigateToCreditNote = useCallback(() => {
    if (generateUrl) {
      transitionTo(generateUrl(EmberRoute.InvoiceCreditNote, originatorId))
    }
  }, [generateUrl, originatorId, transitionTo])

  const handleVoidInvoice = useCallback(() => {
    if (isInvoiceCreditNote) {
      openModal(voidCreditNoteModalId)
    } else {
      openModal(voidInvoiceModalId)
    }
  }, [isInvoiceCreditNote, openModal, voidCreditNoteModalId, voidInvoiceModalId])

  const handleVoidInvoiceSubmit = useCallback(() => {
    voidInvoice(originatorId)
  }, [originatorId, voidInvoice])

  const registerPaymentForInvoice = useCallback(() => {
    openModal(invoiceReconciliationModalId)
  }, [invoiceReconciliationModalId, openModal])

  const handleVoidBill = useCallback(() => {
    queryClient.invalidateQueries([QueryKeys.Transaction, transactionId])
  }, [transactionId])

  const handleVoidCreditNote = useCallback(() => {
    queryClient.invalidateQueries([QueryKeys.Transaction, transactionId])
  }, [transactionId])

  const handleCopyTransactionLink = useCallback(() => {
    if (!generateUrl) {
      return
    }

    copy(`${window.location.origin}${generateUrl(EmberRoute.Transaction, transactionId)}`)
    track('xxx - bookkeeping - QuickAction Clicked', {
      list: 'Transactions',
      button: TransactionsCommonStaticActionType.CopyLink,
    })
  }, [copy, generateUrl, track, transactionId])

  const handleSelect = useCallback(
    <TValue extends string>(value: TValue) => {
      if (value === TransactionsCommonStaticActionType.CopyLink) {
        handleCopyTransactionLink()
        return
      }

      const handler = resolveStaticActionHandler(originatorType, value)
      if (handler) {
        handler({
          originatorId,
          transactionId,
          organizationUrl,
          locationSearch: location.search,
          transitionTo,
          generateUrl,
          handleCopyToDaybook,
          handleRegisterPaymentForBill,
          handleVoidInvoice,
          registerPaymentForInvoice,
          openModal,
          voidBillPaymentModalId,
          voidBillModalId,
          voidDaybookTransactionModalId,
          reverseDaybookTransactionModalId,
          voidVatDeclarationPaymentModalId,
          voidVatDeclarationModalId,
          track,
        })
      }
    },
    [
      handleCopyTransactionLink,
      originatorType,
      originatorId,
      transactionId,
      organizationUrl,
      location.search,
      transitionTo,
      generateUrl,
      handleCopyToDaybook,
      handleRegisterPaymentForBill,
      handleVoidInvoice,
      registerPaymentForInvoice,
      openModal,
      voidBillPaymentModalId,
      voidBillModalId,
      voidDaybookTransactionModalId,
      reverseDaybookTransactionModalId,
      voidVatDeclarationPaymentModalId,
      voidVatDeclarationModalId,
      track,
    ],
  )

  const modals = (
    <>
      {bill && <BillReconciliationModal subject={bill} id={billReconciliationModalId} />}
      {invoice && <InvoiceReconciliationModal subject={invoice} id={invoiceReconciliationModalId} />}
      <VoidBillConfirmationModal id={voidBillModalId} billId={originatorId} onSuccess={handleVoidBill} />
      <VoidInvoiceModal
        id={voidInvoiceModalId}
        invoiceId={originatorId}
        onInvoiceVoid={handleVoidInvoiceSubmit}
        onNavigateToCreditNote={handleNavigateToCreditNote}
      />
      <VoidBillPaymentConfirmationModal paymentId={originatorId} id={voidBillPaymentModalId} />
      <VoidCreditNoteModal id={voidCreditNoteModalId} creditNoteId={originatorId} onSuccess={handleVoidCreditNote} />

      <ModalConfirmation
        id={voidVatDeclarationPaymentModalId}
        cancelLabel={t('vat_declarations.void_payment_modal.cancel')}
        danger
        message={t('vat_declarations.void_payment_modal.message')}
        okLabel={t('vat_declarations.void_payment_modal.approve')}
        okLoading={isProcessingVoidPayment}
        onOk={handleVoidVatDeclarationPaymentClick}
        title={t('vat_declarations.void_payment_modal.title')}
      />

      <ModalConfirmation
        id={voidVatDeclarationModalId}
        cancelLabel={t('vat_declarations.void_settlement_modal.cancel')}
        danger
        message={t('vat_declarations.void_settlement_modal.message')}
        okLabel={t('vat_declarations.void_settlement_modal.approve')}
        okLoading={isProcessingVoidSettlement}
        onOk={handleVoidVatDeclarationClick}
        title={t('vat_declarations.void_settlement_modal.title')}
      />

      <ModalConfirmation
        id={voidDaybookTransactionModalId}
        cancelLabel={t('cancel')}
        danger
        message={t('journal.void_modal.text')}
        okLabel={t('journal.void_modal.yes')}
        okLoading={isProcessingDaybookTransactionAction}
        onOk={handleVoidDaybookTransaction}
        title={t('journal.void_modal.title')}
      />

      <ModalConfirmation
        id={reverseDaybookTransactionModalId}
        cancelLabel={t('cancel')}
        danger
        message={t('journal.reverse_modal.text')}
        okLabel={t('journal.reverse_modal.yes')}
        okLoading={isProcessingDaybookTransactionAction}
        onOk={handleReverseDaybookTransaction}
        title={t('journal.reverse_modal.title')}
      />
    </>
  )

  return { handleSelect, items, modals }
}
